import React, { useEffect } from "react";
import { Checklist as UIChecklist } from "../../../components/ui";

/**
 * Payout timesheet checklist: requirements for payout review to confirm and submit.
 * Uses generic Checklist from ui with payout-timesheet-specific conditions.
 * Sets isSubmittable in parent when all requirements are met.
 *
 * @param {Object} props Component props.
 * @param {boolean} [props.workerTimesheet=false] Worker timesheet exists.
 * @param {boolean} [props.hoursLog=false] Hours log exists.
 * @param {boolean} [props.hasConflicts=false] Unresolved payout conflicts.
 * @param {boolean} [props.missingLogs=false] Missing hours-log rows.
 * @param {boolean} [props.isEditing=false] Review is still in edit mode.
 * @param {boolean} [props.isPayoutSigned=false] Payout signature present.
 * @param {Function} [props.setIsSubmittable] Called when all conditions are met.
 * @param {boolean} [props.maxJobsExceeded=false] Too many jobs on the week.
 * @param {string} [props.className] Extra wrapper classes.
 * @returns {JSX.Element}
 */
const Checklist = ({
  workerTimesheet = false,
  hoursLog = false,
  hasConflicts = false,
  missingLogs = false,
  isEditing = false,
  isPayoutSigned = false,
  setIsSubmittable = () => {},
  maxJobsExceeded = false,
  className,
}) => {
  useEffect(() => {
    setIsSubmittable(
      workerTimesheet && hoursLog && !isEditing && isPayoutSigned && !maxJobsExceeded
    );
  }, [
    workerTimesheet,
    hoursLog,
    isEditing,
    isPayoutSigned,
    maxJobsExceeded,
    setIsSubmittable,
  ]);

  const conditions = [
    {
      met: !(hasConflicts && isEditing),
      message: "Manager hours log and worker hours mismatch detected.",
    },
    {
      met: !(missingLogs && isEditing),
      message: "Manager hours logs not found for some shifts.",
    },
    {
      met: !isEditing,
      message: "Please finish editing and confirm the payout version.",
    },
    {
      met: isPayoutSigned || isEditing,
      message: "Please sign the payout timesheet.",
    },
    {
      met: !maxJobsExceeded,
      message:
        "Max jobs supported by the PDF format has been exceeded. Please use 5 or less unique jobs.",
    },
  ];

  return (
    <UIChecklist
      className={`${className} p-2 pb-0`}
      conditions={conditions}
      successMessage="All requirements met! You can now submit the payout review."
    />
  );
};

export default Checklist;
